// /join/:code: where a pasted invite link lands.
//
// Three people open the same link, and each needs a different next step:
//   - no account:       Create account (the code rides along and is prefilled)
//   - account, signed out: Log in, then come straight back here
//   - signed in:        Join the group, right now, with no new account
//
// Public (see PUBLIC_PATHS in main.ts) and bare (BARE_PATTERNS in chrome.ts): the
// visitor may have no session, and the signed-in nav around it would be links
// that bounce to /login. It carries its own way onward.
//
// A signed-in join never sets a parent: only a brand-new account has one to set,
// and that happens at signup, not here.

import { ApiClientError, joinByInviteLink, previewInviteLink } from '../api/client.js';
import { getRefreshToken } from '../api/tokens.js';
import { el } from '../dom.js';
import { navigate, type View } from '../router.js';
import { authBrand } from './authBrand.js';

export const joinView: View = async (outlet, ctx) => {
  const code = ctx.params['code'] ?? '';
  const card = el('main', { class: 'auth-card' }, [authBrand('div'), el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(card);

  let preview;
  try {
    preview = await previewInviteLink(code);
  } catch (err) {
    // A throttled or unreachable preview is not proof the link is dead, so it
    // does not claim so.
    card.replaceChildren(
      authBrand('div'),
      el(
        'p',
        { class: 'form-error' },
        err instanceof ApiClientError ? err.message : 'We could not check this link. Try again.',
      ),
    );
    return;
  }

  if (!preview.valid) {
    card.replaceChildren(
      authBrand('div'),
      el('h1', {}, 'This invite link is not valid'),
      el(
        'p',
        {},
        'It may have been turned off, or the group no longer lets members invite people. ' +
          'Ask whoever sent it to share a fresh one.',
      ),
      el('p', {}, [el('a', { href: '/' }, 'Go to MySocialCal')]),
    );
    return;
  }

  const who = preview.inviterName ? `${preview.inviterName} invited you to join` : 'You are invited to join';
  const heading = el('h1', {}, preview.groupName);
  const lead = el('p', {}, who + ` ${preview.groupName}.`);

  if (getRefreshToken()) {
    const error = el('p', { class: 'form-error', hidden: true });
    const join = el('button', { type: 'button', class: 'btn' }, 'Join this group');
    join.addEventListener('click', async () => {
      error.hidden = true;
      join.setAttribute('disabled', 'disabled');
      try {
        const result = await joinByInviteLink(code);
        navigate(`/groups/${result.groupId}`);
      } catch (err) {
        error.textContent =
          err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
        error.hidden = false;
        join.removeAttribute('disabled');
      }
    });
    card.replaceChildren(authBrand('div'), heading, lead, error, join);
    return;
  }

  const next = encodeURIComponent(`/join/${code}`);
  card.replaceChildren(
    authBrand('div'),
    heading,
    lead,
    el('p', {}, [
      el('a', { class: 'btn', href: `/register?code=${encodeURIComponent(code)}` }, 'Create an account'),
    ]),
    el('p', {}, [
      'Already have one? ',
      el('a', { href: `/login?next=${next}` }, 'Log in to join'),
    ]),
  );
};
